import { CustomCellDate, CrudLink } from "@/shared/components";
import type { DocumentRow } from "./types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { Chip, Stack, Typography } from "@mui/material";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";

/**
 * Конфигурация UI для feature управления документами
 */

// Конфигурация колонок таблицы
export const getColumnsConfig = (): ColumnDefinition<DocumentRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "title",
    label: "Название",
    options: {
      customBodyRender: (data: unknown) => {
        const document = data as DocumentRow;
        return (
          <CrudLink id={document.id} tab="details">
            {document.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "slug",
    label: "Slug",
    options: {
      setCellHeaderProps: () => ({ style: { width: 200 } }),
    },
  },
  {
    name: "type",
    label: "Тип",
    options: {
      setCellHeaderProps: () => ({ style: { width: 100 } }),
    },
  },
  {
    name: "platform",
    label: "Платформа",
    options: {
      // DataTable передаёт позиционное значение колонки (row[columnIndex],
      // см. mapColumns), а не всю строку — здесь это уже сам
      // document.platform (или null), а не DocumentRow.
      customBodyRender: (data: unknown) => {
        const platform = data as DocumentRow["platform"];
        if (!platform) {
          return <span style={{ color: "#999" }}>Глобальный</span>;
        }
        return platform.title;
      },
    },
  },
  {
    name: "language",
    label: "Язык",
    options: {
      customBodyRender: (data: unknown) => {
        const language = data as DocumentRow["language"];
        if (!language) {
          return <span style={{ color: "#999" }}>Любой</span>;
        }
        return language.title;
      },
    },
  },
  {
    name: "isPublished",
    label: "Опубликован",
    options: {
      setCellHeaderProps: () => ({ style: { width: 120 } }),
      customBodyRender: (value: unknown) => (
        <span style={{ color: value ? "green" : "red" }}>
          {value ? "Да" : "Нет"}
        </span>
      ),
    },
  },
  {
    name: "createdAt",
    label: "Дата создания",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
];

// Конфигурация текстов интерфейса
export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Документы",
  itemCreated: "Документ создан",
  itemUpdated: "Документ обновлен",
  createItem: "Создать документ",
  refetchList: "Обновить список",
  deleteItems: "Удалить документы",
  itemsDeleted: "Документы удалены",
  restoreItems: "Восстановить документы",
  itemsRestored: "Документы восстановлены",
  cancel: "Отмена",
  editItem: "Документ",
  showTitle: "Детали",
});

// Колонки для мобильного представления: название со slug и признак публикации
export const getMobileColumnsConfig = (): DataListViewColumns<DocumentRow> => ({
  main: {
    title: "Документ",
    sx: { flex: 1, minWidth: 0 },
    renderCell: (row) => (
      <Stack sx={{ minWidth: 0 }}>
        <CrudLink id={row.id} tab="details" noWrap>
          {row.title}
        </CrudLink>
        <Typography variant="body2" color="textSecondary" noWrap>
          {row.slug} · {row.platform?.title ?? "Глобальный"}
        </Typography>
      </Stack>
    ),
  },
  isPublished: {
    title: "Статус",
    sx: { width: 108, flexShrink: 0 },
    renderCell: (row) => (
      <Chip
        label={row.isPublished ? "Опубликован" : "Черновик"}
        color={row.isPublished ? "success" : "default"}
        size="small"
      />
    ),
  },
});
